import { type DataTableColumn } from '@odo/shared-ui';
import {
    type WorkQueueCase,
    type StatusMeta,
    type OutgoingAction,
    type SortBy
} from '../../services/workQueue';
import { ManufacturerStatusPill } from './ManufacturerStatusPill';
import { metaFor } from './versionedStatusMeta';
import { ActionCell, AssigneeCell, DmCell, PatientCell, VaCountsCell } from './caseCells';
import { TimeCell } from '../shared/TimeCell';
import {
    MILESTONE_FIELD_TO_COLUMN,
    resolveLatenessTier,
    type MilestoneField
} from './timeThresholds';

export interface CaseMilestoneColumn {
    label: string;
    sortKey: SortBy;
    field: MilestoneField;
}

export interface CaseColumnOptions {
    showAlignerColumn: boolean;
    showManufacturerColumn: boolean;
    showManufacturerStatusColumn: boolean;
    showRefinementsColumn: boolean;
    showVaCountsColumn: boolean;
    milestones: ReadonlyArray<CaseMilestoneColumn>;
    statusLookup: Map<string, StatusMeta>;
    onAction: (c: WorkQueueCase, outgoingAction: OutgoingAction) => void;
    onStartTask: (id: string) => void;
}

const CELL = 'px-3 py-0 overflow-hidden';
const HEADER = 'px-3';
// Placeholder overwritten at the end - PATIENT takes 30%, the rest share evenly.
const AUTO_WIDTH = '';

/**
 * The work-queue case columns for `DataTable`, assembled per step: the extra
 * DM/Manufacturer/Status column, milestone dates and the refinements count
 * are inserted between ASSIGNEE and PHASE exactly as the legacy table did.
 */
export function buildCaseColumns(
    opts: CaseColumnOptions
): DataTableColumn<WorkQueueCase, SortBy>[] {
    const { statusLookup, onAction, onStartTask } = opts;

    const columns: DataTableColumn<WorkQueueCase, SortBy>[] = [
        {
            key: 'patient',
            label: 'PATIENT',
            width: '30%',
            sortable: true,
            sortKey: 'name',
            cellClassName: 'pl-4 pr-3 py-0 overflow-hidden',
            headerClassName: 'pl-4 pr-3',
            render: (c) => <PatientCell workCase={c} />
        },
        {
            key: 'assignee',
            label: 'ASSIGNEE',
            width: AUTO_WIDTH,
            // No overflow-hidden: the dropdown renders in-place and would be
            // clipped invisible. Clicks must not select the row underneath.
            cellClassName: 'px-3 py-0',
            headerClassName: HEADER,
            stopClickPropagation: true,
            render: (c) => <AssigneeCell workCase={c} />
        },
        ...(opts.showAlignerColumn
            ? [
                  {
                      key: 'dm',
                      label: 'DM',
                      width: AUTO_WIDTH,
                      sortable: true as const,
                      sortKey: 'aligner-progress' as const,
                      cellClassName: CELL,
                      headerClassName: HEADER,
                      render: (c: WorkQueueCase) => <DmCell workCase={c} />
                  }
              ]
            : []),
        ...(opts.showVaCountsColumn
            ? [
                  {
                      key: 'va-counts',
                      label: 'VAs',
                      width: AUTO_WIDTH,
                      cellClassName: CELL,
                      headerClassName: HEADER,
                      render: (c: WorkQueueCase) => <VaCountsCell workCase={c} />
                  }
              ]
            : []),
        ...(opts.showManufacturerColumn
            ? [
                  {
                      key: 'manufacturer',
                      label: 'MANUFACTURER',
                      width: AUTO_WIDTH,
                      cellClassName: CELL,
                      headerClassName: HEADER,
                      render: (c: WorkQueueCase) => (
                          <span className="block truncate text-sm text-gray-700">
                              {c.manufacturer || ''}
                          </span>
                      )
                  }
              ]
            : []),
        ...(opts.showManufacturerStatusColumn
            ? [
                  {
                      // Labeled just "STATUS": the full header forced the column
                      // wide enough to break the row layout at narrow widths.
                      key: 'manufacturer-status',
                      label: 'STATUS',
                      width: AUTO_WIDTH,
                      cellClassName: CELL,
                      headerClassName: HEADER,
                      render: (c: WorkQueueCase) =>
                          c.manufacturerCaseStatus ? (
                              <ManufacturerStatusPill status={c.manufacturerCaseStatus} />
                          ) : null
                  }
              ]
            : []),
        ...opts.milestones.map((m) => ({
            key: `milestone-${m.field}`,
            label: m.label,
            width: AUTO_WIDTH,
            sortable: true as const,
            sortKey: m.sortKey,
            cellClassName: `${CELL} leading-tight`,
            headerClassName: HEADER,
            // Milestones are date-only (ISO at UTC midnight): format in UTC so
            // the displayed day matches the stored date in every timezone.
            render: (c: WorkQueueCase) => (
                <TimeCell
                    iso={c[m.field]}
                    tier={resolveLatenessTier(
                        c[m.field],
                        MILESTONE_FIELD_TO_COLUMN[m.field],
                        c.statusId
                    )}
                    utc
                />
            )
        })),
        ...(opts.showRefinementsColumn
            ? [
                  {
                      key: 'refinements',
                      label: 'REFINEMENTS',
                      width: AUTO_WIDTH,
                      sortable: true as const,
                      sortKey: 'refinements' as const,
                      cellClassName: CELL,
                      headerClassName: HEADER,
                      // null (never synced) means zero completed rounds.
                      render: (c: WorkQueueCase) => (
                          <span className="text-xs text-gray-700 tabular-nums">
                              {c.refinementsCount ?? 0}
                          </span>
                      )
                  }
              ]
            : []),
        {
            key: 'phase',
            label: 'PHASE',
            width: AUTO_WIDTH,
            sortable: true,
            sortKey: 'phase-aging',
            cellClassName: `${CELL} leading-tight`,
            headerClassName: HEADER,
            render: (c) => (
                <TimeCell
                    iso={c.phaseEnteredAt}
                    tier={resolveLatenessTier(c.phaseEnteredAt, 'phase', c.statusId)}
                />
            )
        },
        {
            key: 'step',
            label: 'STEP',
            width: AUTO_WIDTH,
            sortable: true,
            sortKey: 'aging',
            cellClassName: `${CELL} leading-tight`,
            headerClassName: HEADER,
            render: (c) => (
                <TimeCell
                    iso={c.stepEnteredAt}
                    tier={resolveLatenessTier(c.stepEnteredAt, 'step', c.statusId)}
                />
            )
        },
        {
            key: 'action',
            // Named for the sr-only a11y header; headerNode keeps it blank visibly.
            label: 'Actions',
            headerNode: null,
            width: AUTO_WIDTH,
            align: 'right',
            cellClassName: CELL,
            headerClassName: HEADER,
            stopClickPropagation: true,
            render: (c) => (
                <ActionCell
                    workCase={c}
                    statusMeta={metaFor(statusLookup.get(c.statusId), c.version)}
                    onAction={onAction}
                    onStartTask={onStartTask}
                />
            )
        }
    ];

    // Mirror the legacy table-fixed behavior: PATIENT owns 30%, every other
    // column takes an equal share of the remaining width.
    const share = `${(70 / (columns.length - 1)).toFixed(2)}%`;
    return columns.map((column) =>
        column.width === AUTO_WIDTH ? { ...column, width: share } : column
    );
}
